import {CrPersonSuggestion} from '../../../../../../cr/logic/cr-person-suggestion';
import {
    casePersonCapturedIdentity,
    casePersonCarriesIdentity,
    CwCasePersonNameSource,
} from '../../../../../util/cw-case-person-name.util';

/**
 * AI:
 * Status: "in progress"
 * Type: logic
 * SubType: mapping
 * Reason: J2 assignment — the missing projection from an UNLINKED case person onto the delivery shape
 *         `cr-customer-persons` reconciles against a customer's people. Pure, Angular-free, and
 *         deliberately name-only: it hands over the two captured columns and NOTHING else.
 *
 * @ticket TM-1000001991
 */

/**
 * [J2 — Zuordnung] Turns the case persons that link NO `pm.Person` into `CrPersonSuggestion`s.
 *
 * <h3>Why this file exists at all</h3>
 * Two questions are asked about the persons of one case, and they are not the same question:
 * <ul>
 *   <li><b>J1 — Aktualität</b> ("diese Person kennen wir, ist ihr erfasster Stand noch aktuell?") is a
 *       pair comparison over the ~1.068 rows that DO link a person, and it lives next door in
 *       `cw-case-person-check.ts`. It never builds a suggestion and never sees a matcher.</li>
 *   <li><b>J2 — Zuordnung</b> ("wer ist dieser Mensch?") is a SEARCH over the ~139 rows that link
 *       nothing. The search itself already exists, complete, in `cr-customer-persons` and its
 *       `CrPersonMatch`; what did not exist was the one projection that lets a case row enter it.
 *       This file is that projection and is the whole of J2's logic.</li>
 * </ul>
 * Conflating the two is the defect this feature has been corrected out of three times (capability
 * matrix, Fassung 1 and 2): running a LINKED row through the matcher makes it answer "keine
 * Übereinstimmung" for a row whose person is right there, merely under a stale captured name. Hence the
 * hard row-selection rule below, and hence AUFLAGE A3, which additionally demands that the two answers
 * are labelled apart on screen so a J1 finding can never be read as a J2 "kennen wir nicht".
 *
 * <h3>Why a sibling file and not `cw-case-person-check.ts`</h3>
 * That file's own doc states, in as many words, that under the signed Fassung 4 split "J1 never touches
 * `CrPersonSuggestion`, `CrPersonMatch` or the matcher at all". Adding the mapping there would make its
 * first import line contradict its documentation. It sits here instead — flat, in the step folder, next
 * to `cw-case-person-check.ts`, `cw-case-object-step-persons.scope.ts` and
 * `cw-case-person-role-section.model.ts`, which is this module's established convention for logic that
 * belongs to exactly one step (see the placement reasoning in `cw-case-person-check.ts`).
 *
 * <h3>⚠ THE ROLE OF A CASE PERSON NEVER TRAVELS</h3>
 * A case person carries a ROLE AT THE CONTRACT — `typeDtoName` (`cw.CwCaseTypePerson`),
 * `contractPersonTypeDtoName`, `owner`, `insured`. A `CrPersonSuggestion` carries, among other things,
 * `relationType`: the person's RELATIONSHIP TO THE CUSTOMER (Tochter, Ehepartner, …). The two are
 * ORTHOGONAL and mapping one onto the other is a category error with a concrete cost: `relationType`
 * selects which `pm.PersonRelationPerson` comes into being, including the gender-dependent counterpart
 * type `CustomerPersonMatrixService` resolves from it (see `CrPersonSuggestion.relationType`). "Versicherte
 * Person" is not a family relation, and a person filed under one because she was insured on a contract is
 * a wrong record that nobody consciously created.
 *
 * <p>`CR_PERSON_SUGGESTION_ROW_KEYS` — a total `Record` over
 * `Exclude<keyof CrPersonSuggestion, 'reference'>` — means the type would not even LET a role field
 * through under some new name. That is a backstop, not the rule: this file simply never sets any of
 * them, and {@link unlinkedCasePersonSuggestion} is pinned on that by its own spec.
 *
 * <h3>Two of nineteen columns, and no invention</h3>
 * The frontend `CwCasePersonDto` carries exactly 2 of the 19 deprecated snapshot columns —
 * `firstName` and `lastName` (`cw-case-person.dto.ts:44-60`). So a suggestion produced here has a name
 * and nothing else: no birth date, no gender, no birth name. Those are not omitted for tidiness — they
 * are ABSENT FROM THE SOURCE, and inventing them would feed `CrPersonMatch` a triple the case never
 * captured, which is how a matcher is made to bind the wrong human. The consequence is stated rather than
 * hidden: a name-only delivery reaches `unique` only when the customer has exactly one person of that
 * name; otherwise the reviewer decides, which is the correct outcome for the data that exists.
 *
 * <h3>Nothing here reads a snapshot column directly</h3>
 * The identity is read ONLY through `casePersonCapturedIdentity`. `cw-case-person.dto.ts:40-41` permits
 * exactly ONE reader file for `firstName`/`lastName` — `cw-case-person-name.util.ts` — so that dropping
 * the 19 deprecated columns stays a single-file change. This file would have been the forbidden third
 * reader; going through the util is what keeps that promise intact.
 */

/**
 * The minimum shape J2 reads off a case person.
 *
 * <p>Structural rather than a `CwCasePersonDto` on purpose — the same reasoning `CwCasePersonNameSource`
 * gives — so a QL projection or a hand-built fixture works without a cast.
 *
 * <p>Deliberately NOT `CwCasePersonCheckSource` from the sibling file, even though the two overlap: that
 * one additionally carries `personDto`, the linked person's CURRENT stored names, which is precisely the
 * half J2 must never see. A J2 row has no linked person by definition; a source type that offered one
 * would invite exactly the conflation AUFLAGE A3 exists to prevent.
 *
 * @ticket TM-1000001991
 */
export interface CwCasePersonSuggestionSource extends CwCasePersonNameSource {

    /** `DtoDetail.id` of the row — the sole source of {@link CrPersonSuggestion.reference}. */
    id: number;

    /**
     * FK onto the linked `pm.Person`. Read with `typeof personDtoId === 'number'`, NEVER truthiness:
     * `0` is a legal primary key and a truthiness test would hand a LINKED row to the matcher — the
     * Fassung-2 defect (capability matrix, Befund C1.7 / finding R1).
     */
    personDtoId?: number | null;
}

/**
 * [J2] The delivery entry for ONE case person, or `null` when the row is not a J2 candidate.
 *
 * <p>`null` is returned — rather than a partially filled suggestion — for all three exclusions, because
 * they mean three different things and none of them is "a person we could not describe well":
 * <ol>
 *   <li><b>The row links a `pm.Person`.</b> Its question is already answered; it belongs to J1. Handing
 *       it to the matcher is the diagnosed Fassung-2 defect.</li>
 *   <li><b>The row carries no identity</b> ({@code casePersonCarriesIdentity} is `false`): an empty role
 *       slot, not a person. There is nobody to search for.</li>
 *   <li><b>The row carries an identity, but no NAME</b> — possible only through the FK half of
 *       `casePersonCarriesIdentity` on a row whose `personDtoId` is neither a number nor `null` (a
 *       string id from a loosely typed projection). A nameless suggestion cannot be matched
 *       (`CrPersonIdentity` would key it on nothing) and cannot be created, so it is not delivered.
 *       Defensive, and pinned by a spec so it stays that way.</li>
 * </ol>
 *
 * <p><b>What is set:</b> `reference`, and whichever of `firstName` / `lastName` the row actually
 * captured. <b>What is never set, under any circumstances:</b> every remaining field of
 * `CrPersonSuggestion` — above all `relationType`, see the file doc.
 *
 * @ticket TM-1000001991
 * @param row a case person row, possibly absent
 * @return the delivery entry, or `null` when this row is not J2's business
 */
export function unlinkedCasePersonSuggestion(
    row: CwCasePersonSuggestionSource | null | undefined,
): CrPersonSuggestion | null {

    if (row == null) {
        return null;
    }

    // Exclusion 1 — the LINKED rows are J1's. `typeof`, never truthiness (Befund C1.7).
    if (typeof row.personDtoId === 'number') {
        return null;
    }

    // Exclusion 2 — an empty role slot is not a person to search for.
    if (!casePersonCarriesIdentity(row)) {
        return null;
    }

    // TM-1000001991: the ONE permitted reader of the two snapshot columns — never row.firstName here.
    const captured = casePersonCapturedIdentity(row);
    const firstName = nonBlank(captured.firstName);
    const lastName = nonBlank(captured.lastName);

    // Exclusion 3 — see the doc above: an identity that is not a NAME cannot be searched for.
    if (firstName == null && lastName == null) {
        return null;
    }

    // reference is String(id), OPAQUE and unparsed on both sides of the seam — the caller recognises
    // its own row by it and `cr-customer-persons` passes it through unchanged
    // (`cr-person-suggestion.ts:28`, "THIS SEAM IS THE POINT").
    const suggestion: CrPersonSuggestion = {reference: String(row.id)};

    // Assigned only when present, so an absent column stays ABSENT rather than becoming `undefined` —
    // the same rule `CrPersonSuggestionParser.assign` follows, and what keeps `toSeedValues` from
    // blanking a field by seeding it with nothing.
    if (firstName != null) {
        suggestion.firstName = firstName;
    }
    if (lastName != null) {
        suggestion.lastName = lastName;
    }

    return suggestion;
}

/**
 * [J2] The delivery for a whole case — every UNLINKED row that carries a name, in the input's order.
 *
 * <p>The row-selection rule is applied ONCE, here, so no caller re-derives it. A row that is excluded is
 * simply not in the result: there is no placeholder entry and no marker, because a delivery is a list of
 * people to look for and a row that is not one of them is not an under-specified person.
 *
 * <p>De-duplication is deliberately NOT done. Two case rows naming the same human are two rows, and
 * `reference` keeps them apart; collapsing them here would silently decide something for the reviewer
 * that `CrPersonMatch` explicitly refuses to decide ("it does not de-duplicate a delivery against
 * itself", `cr-person-match.ts`).
 *
 * @ticket TM-1000001991
 * @param casePersons the case's persons, in any order; `null`/`undefined` yields an empty delivery
 * @return one {@link CrPersonSuggestion} per unlinked, named row, in the input's order
 */
export function unlinkedCasePersonSuggestions(
    casePersons: readonly CwCasePersonSuggestionSource[] | null | undefined,
): CrPersonSuggestion[] {

    const suggestions: CrPersonSuggestion[] = [];

    (casePersons ?? []).forEach(row => {
        const suggestion = unlinkedCasePersonSuggestion(row);
        if (suggestion != null) {
            suggestions.push(suggestion);
        }
    });

    return suggestions;
}

/** The trimmed value, or `null` when it is absent or whitespace only. */
function nonBlank(value: string | null | undefined): string | null {

    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();

    return trimmed.length === 0 ? null : trimmed;
}
